import { useTranslation } from 'i18next-vue'
import { useDateFormat } from '@vueuse/core'
import { format as formatDateFn, formatDistanceToNow, isValid, parse, parseISO } from 'date-fns'
import { de, enUS } from 'date-fns/locale'
import { unref } from 'vue'

/**
 * Composable for date formatting with i18n support
 * Uses VueUse's useDateFormat for consistent, localized formatting
 */
export function useDate() {
    const { i18next } = useTranslation()

    /**
     * Get the current locale from i18next
     * Falls back to 'de-DE' if not available
     */
    const getLocale = (): string => {
        const lang = i18next.language || 'de'
        // Convert i18next language codes to locale codes
        const localeMap: Record<string, string> = {
            de: 'de-DE',
            en: 'en-US',
        }
        return localeMap[lang] || 'de-DE'
    }

    // WHY: We already add a helper to get the locale from i18next but at the moment we only need to support the locale 'de'
    const getFnsLocale = (): typeof de => {
        if (i18next.language === 'de') {
            return de
        }
        return de
    }

    /**
     * Formats a date string to localized date and time format
     * @param date - ISO date string
     * @returns Formatted date string (e.g., "07.10.2025, 14:30")
     */
    const formatDate = (date: string): string => {
        const locale = getLocale()
        return unref(useDateFormat(date, 'DD.MM.YYYY, HH:mm', { locales: locale }))
    }

    /**
     * Formats a date string to localized date only (without time) or return an empty string if no date is given
     * @param date - ISO date string or Date object or undefined
     * @returns Formatted date string (e.g., "07.10.2025")
     */
    const formatDateOnly = (date: string | Date | undefined): string => {
        if (!date) return ''

        const locale = getLocale()
        return unref(useDateFormat(date, 'DD.MM.YYYY', { locales: locale }))
    }

    /**
     * Formats a date string to localized time only
     * @param date - ISO date string
     * @returns Formatted time string (e.g., "14:30")
     */
    const formatTimeOnly = (date: string): string => {
        const locale = getLocale()
        return unref(useDateFormat(date, 'HH:mm', { locales: locale }))
    }

    /**
     * Formats a Date object to ISO date string for API (YYYY-MM-DD)
     * @param date - Date object
     * @returns ISO date string (e.g., "2025-10-07")
     */
    const formatDateForAPI = (date: Date): string => {
        return formatDateFn(date, 'yyyy-MM-dd')
    }

    /**
     * Formats a Date object to ISO datetime string for API in UTC
     * @param date - Date object
     * @returns ISO UTC string e.g., "2025-10-07T14:30:00Z"
     */
    const formatDateTimeForAPI = (date: Date): string => {
        return date.toISOString()
    }

    /**
     * Serializes local calendar + clock fields as a stable ISO datetime.
     *
     * For UI-picked local date/time values we treat the selected wall time
     * as the canonical value. Converting via Date.toISOString() shifts the day
     * around midnight in positive offsets. By re-encoding local fields as UTC
     * components, the resulting ISO string preserves the visible date/time.
     */
    const formatLocalDateTimeForAPI = (date: Date): string => {
        return date.toISOString()
    }

    /**
     * Serializes local calendar without time information as a stable ISO datetime.
     *
     * This helper method is normall only used in the event step because the user is able to define a date only value
     * or a date and time value. In both cases we want to persist the date as a stable ISO string because both values
     * are stored in a strapi date time field.
     */
    const formatLocalTimeDateOnlyForAPI = (date: Date): string => {
        const localFieldsAsUTC = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
        )

        return localFieldsAsUTC.toISOString()
    }

    /**
     * Parses an API datetime string as local with time, without applying timezone offsets.
     *
     * The backend persists UI-selected local date/time fields as ISO strings. When we later
     * hydrate them with `new Date(...)`, the browser applies the timezone offset again and the
     * visible clock time shifts. Here we intentionally ignore the serialized timezone marker and
     * rebuild a local Date from the literal calendar + clock fields.
     */
    const parseDatetimeFromAPI = (dateString: string): Date | null => {
        if (!dateString) return null

        const date = parseISO(dateString)
        return isValid(date) ? date : null
    }

    /**
     * Formats a date as relative time (e.g., "vor 2 Stunden", "2 hours ago")
     * Uses date-fns formatDistanceToNow for localized relative time
     * @param date - ISO date string or Date object
     * @returns Relative time string with i18n support
     */
    const formatRelativeTime = (date: string | Date | undefined): string => {
        if (!date) return ''

        const locale = getLocale()
        const dateLocale = locale === 'de-DE' ? de : enUS

        return formatDistanceToNow(new Date(date), {
            addSuffix: true,
            locale: dateLocale,
        })
    }

    /**
     * Parses HH:mm time string to Date object
     * @param timeString - Time string in HH:mm format (e.g., "14:30")
     * @returns Date object with today's date and the specified time, or null if invalid
     */
    const parseTimeString = (timeString: string): Date | null => {
        if (!timeString || timeString.trim() === '') return null

        const parsedResultDate = parse(timeString, 'HH:mm', new Date())

        // We need to check if the parsed date is NaN because if the parsing failed
        // date-fns return still a date object but with an invalid value as return of "getTime()".
        // It means we handle this here and return this case null
        return isNaN(parsedResultDate.getTime()) ? null : parsedResultDate
    }

    /**
     * Formats Date object to HH:mm time string
     * @param date - Date object
     * @returns Time string in HH:mm format (e.g., "14:30"), or empty string if null
     */
    const formatTimeString = (date: Date | null): string => {
        if (!date) return ''
        return unref(useDateFormat(date, 'HH:mm'))
    }

    return {
        formatDate,
        formatDateOnly,
        formatTimeOnly,
        formatDateForAPI,
        formatLocalTimeDateOnlyForAPI,
        formatDateTimeForAPI,
        formatLocalDateTimeForAPI,
        parseDatetimeFromAPI,
        formatRelativeTime,
        parseTimeString,
        formatTimeString,
        getLocale,
        getFnsLocale,
    }
}
